//
// # EditorTab
//
// Components for individual editor tab panels in the dockview layout.
//
// EditorTabHeader — Custom tab header showing design name, dirty indicator,
//   close button, and middle-click-to-close. Rendered by dockview's native
//   tab bar.
//
// EditorTabPanel — Panel content that attaches/detaches the editor's canvas
//   container to the DOM. Each open design gets its own EditorTabPanel
//   instance.
//

import React, { useEffect, useRef } from "react";
import {
  type IDockviewPanelProps,
  type IDockviewPanelHeaderProps,
} from "dockview-react";
import { X } from "lucide-react";
import { Ident } from "GenAlphaModel";
import { useWorkspace, useWorkspaceSnapshot } from "./context";

// Custom tab header for editor panels — shows design name, dirty indicator,
// and a close button. Rendered by dockview's native tab bar.
export function EditorTabHeader({ api }: IDockviewPanelHeaderProps) {
  const workspace = useWorkspace();
  const { tabs } = useWorkspaceSnapshot();
  // Panel ID == Ident.stringify(ident), so look up the tab info
  const identKey = api.id;
  const tab = tabs.find((t) => Ident.stringify(t.ident) === identKey);

  return (
    <div
      className="ide-editor-tab-header"
      onMouseDown={(e) => {
        // Middle-click closes the tab (standard IDE behaviour)
        if (e.button === 1 && tab) {
          e.preventDefault();
          workspace.close(tab.ident);
        }
      }}
    >
      <span className="ide-editor-tab-label">
        {tab?.displayName ?? api.title}
        {tab?.isDirty ? " *" : ""}
      </span>
      <button
        className="ide-editor-tab-close"
        onClick={(e) => {
          e.stopPropagation();
          if (tab) workspace.close(tab.ident);
        }}
      >
        <X size={12} />
      </button>
    </div>
  );
}

// Panel component for an individual editor tab.
// Each open design gets its own EditorTabPanel, which attaches/detaches
// the editor's canvas container to the DOM.
export function EditorTabPanel({ api }: IDockviewPanelProps) {
  const workspace = useWorkspace();
  const identKey = api.id;
  const hostRef = useRef<HTMLDivElement>(null);

  // Attach the editor's container div and set up resize observation
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const container = workspace.getContainerForKey(identKey);
    if (!container) return;

    host.appendChild(container);

    const editor = workspace.getEditorForKey(identKey);
    if (editor) {
      editor.canvas.sizeToElement(host);
      // Fit content to the viewport now that the canvas has correct panel dimensions.
      // fit() is deferred until here so the container is in the DOM with proper sizing.
      editor.fit();
    }

    const observer = new ResizeObserver(() => {
      const ed = workspace.getEditorForKey(identKey);
      if (ed) {
        ed.canvas.sizeToElement(host);
      }
    });
    observer.observe(host);

    return () => {
      observer.disconnect();
      // Detach but DO NOT destroy the container
      if (container.parentElement === host) {
        host.removeChild(container);
      }
    };
  }, [identKey, workspace]);

  // Re-attach when the panel becomes visible again (e.g., tab switched back)
  useEffect(() => {
    const disposable = api.onDidVisibilityChange(
      ({ isVisible }: { isVisible: boolean }) => {
        const host = hostRef.current;
        if (!host || !isVisible) return;
        const container = workspace.getContainerForKey(identKey);
        if (container && container.parentElement !== host) {
          host.appendChild(container);
          const editor = workspace.getEditorForKey(identKey);
          if (editor) {
            editor.canvas.sizeToElement(host);
          }
        }
      }
    );
    return () => disposable.dispose();
  }, [api, identKey, workspace]);

  return <div ref={hostRef} className="ide-editor-pane" />;
}
